import { FieldDataType } from '../enums/FieldDataType';
import { FieldValidationType } from '../enums/FieldValidationType';
import { validateContactFieldValue } from '../utils/formValidationHelpers';

export type ContactAddress = {
    addressId?: number;
    street: string;
    country: string;
    province: string;
    city: string;
    postalCode: string;
};

/* describes the possible values held by a contact field's value property */
export type ContactFieldValue = string | ContactAddress;

export type ContactField<T = ContactFieldValue> = {
    /*
        label, isUnique, isFilterable are provided in request response objects but should
        not be trusted: these values can all be changed in custom field settings.
        TIP: if needing these values: extend object by matching to corresponding client field using fieldId
    */
    fieldType: FieldDataType;
    validationType: FieldValidationType;
    fieldId: number;
    label: string;
    isPrimary: boolean;
    isReadOnly: boolean;
    isUnique: boolean;
    isFilterable: boolean;
    rowId: number;
    leadId: number;
    isVerifiable: boolean;
    isVerified: boolean;
    value: T;
};

export type CreateContactFieldRequest = {
    fieldType: FieldDataType;
    fieldId: number;
    leadId: number;
    value: ContactFieldValue;
    isVerified: boolean;
};

/**
 * buildCreateContactField
 *
 * @description
 * Builds an object matching that expected by the server for a create
 * contact field request.
 */
export function buildCreateContactField(args: {
    fieldName: string;
    /* 
        fieldNameproperty is expected to ensure the validator gives an accurate error message, 
        not to be included in request
     */
    fieldType: FieldDataType;
    fieldId: number;
    leadId: number;
    value: ContactFieldValue;
    isVerified: boolean;
}): CreateContactFieldRequest {
    return {
        fieldType: args.fieldType,
        fieldId: args.fieldId,
        leadId: args.leadId,
        value: validateContactFieldValue(args.fieldName, args.value),
        isVerified: args.isVerified,
    };
}

export type UpdateContactFieldRequest = {
    fieldType: FieldDataType;
    fieldId: number;
    leadId: number;
    rowId: number;
    value: ContactFieldValue;
    isVerifiable: boolean;
    isVerified: boolean;
};

/**
 * buildUpdateContactField
 *
 * @description
 * Builds an object matching that expected by the server for an update
 * contact field request.
 */
export function buildUpdateContactField(
    args: ContactField & {
        fieldName: string;
        /*
        fieldName property is expected to ensure the validator gives an accurate error message,
        not to be included in request
     */
    },
): UpdateContactFieldRequest {
    return {
        fieldType: args.fieldType,
        fieldId: args.fieldId,
        leadId: args.leadId,
        rowId: args.rowId,
        value: validateContactFieldValue(args.fieldName, args.value),
        isVerifiable: args.isVerifiable,
        isVerified: args.isVerified,
    };
}

export type DeleteContactFieldRequest = {
    fieldId: number;
};

export type ContactFieldsGroupedByValidationType = {
    address: ContactField<ContactAddress>[];
    date: ContactField<string>[];
    dollar: ContactField<string>[];
    email: ContactField<string>[];
    none: ContactField<string>[];
    other: ContactField<string>[];
    phone: ContactField<string>[];
    year: ContactField<string>[];
};

/* "null" is added to the usual contact field values here to accomodate empty Mui-Pickers state */
export interface FormFieldCreateContact<T = ContactFieldValue | null> {
    validationType: FieldValidationType;
    fieldId: number;
    value: T;
    label: string;
}

export type CreateContactFieldsGroupedByValidationType = {
    address: FormFieldCreateContact<ContactAddress>[];
    date: FormFieldCreateContact<string | null>[];
    dollar: FormFieldCreateContact<string | null>[];
    email: FormFieldCreateContact<string | null>[];
    none: FormFieldCreateContact<string | null>[];
    other: FormFieldCreateContact<string | null>[];
    phone: FormFieldCreateContact<string | null>[];
    year: FormFieldCreateContact<string | null>[];
};

/**
 * buildFormFieldCreateContact
 *
 * @descripton
 * Builds an object matching the one used by the Create Contact form
 */
export function buildFormFieldCreateContact<
    T extends {
        validationType: FieldValidationType;
        fieldId: number;
        label: string;
    },
>(field: T): FormFieldCreateContact {
    let fieldValue: ContactAddress | string | null;

    switch (field.validationType) {
        case FieldValidationType.address:
            fieldValue = {
                street: '',
                city: '',
                province: '',
                country: '',
                postalCode: '',
            };
            break;
        case FieldValidationType.date:
            /* Requires null to show empty value in Mui Date Picker component */
            fieldValue = null;
            break;
        default:
            fieldValue = '';
    }

    return {
        validationType: field.validationType,
        fieldId: field.fieldId,
        label: field.label,
        value: fieldValue,
    };
}
